import {
    getToken, GetTokenGeneratorType, Nullable, safeSaga,
} from "@data-force/app";
import axios, {AxiosResponse} from "axios";
import {
    all, put, PutEffect, select, SelectEffect, takeLatest,
} from "redux-saga/effects";
import {SetLoadingAction} from "../../interfaces/store/actions/mask";
import {JobEntity} from "../../interfaces/store/actions/payload/job";
import {FetchTasksData, Task} from "../../interfaces/store/actions/payload/task";
import {GetTaskInfoAction, GetTaskListAction, UpdateTaskListAction} from "../../interfaces/store/actions/task";
import {Cursor} from "../../interfaces/store/cursor";
import {GetTaskListResponse, TaskInfo} from "../../interfaces/store/sagas/task";
import StoreState from "../../interfaces/store/storeState";
import {GET_TASK_INFO, GET_TASK_LIST} from "../actions/availableActions";
import {setLoading} from "../actions/mask";
import {updateTaskList} from "../actions/task";
import {getAnnotationUrl, getBaseUrl} from "./main";

type FetchTasksType = Generator<
Promise<AxiosResponse<Nullable<GetTaskListResponse>>> | SelectEffect,
AxiosResponse<Nullable<GetTaskListResponse>>
>;

function* fetchTasks({
    projectId, jobId, phaseName, cursor,
}: FetchTasksData): FetchTasksType {
    const baseUrl: Nullable<string> = (yield getBaseUrl()) as Nullable<string>;
    return (yield axios.get(
        `${baseUrl}/project/${projectId}/job/${jobId}/task`, {
            params: {
                phaseName,
                ...cursor,
            },
        },
    )) as AxiosResponse<Nullable<GetTaskListResponse>>;
}

export type FetchAllTasksType = Generator<
SelectEffect | FetchTasksType | FetchAllTasksType,
Task[] | undefined>;

export function* fetchAllTasks(
    tasks: Task[] = [],
    tasksCursor: Nullable<Partial<Cursor>> = {},
): FetchAllTasksType {
    const selectedJob: Nullable<JobEntity> = (yield select(
        (state: StoreState): Nullable<JobEntity> => state.job.selectedJob,
    )) as Nullable<JobEntity>;
    if (!selectedJob) {
        return [];
    }
    const {projectId, jobId, name: phaseName}: JobEntity = selectedJob;

    const response: Nullable<GetTaskListResponse> = ((yield fetchTasks({
        cursor: tasksCursor,
        projectId,
        jobId,
        phaseName,
    })) as AxiosResponse<Nullable<GetTaskListResponse>>).data;

    if (response) {
        const {items, cursor}: GetTaskListResponse = response;

        if (cursor) {
            return (yield fetchAllTasks(
                tasks.concat(items || []),
                {...cursor},
            )) as Task[] | undefined;
        }
    }

    return tasks.concat(response?.items || []);
}

type GetTaskListIteratorType =
    SelectEffect
    | Promise<AxiosResponse<GetTaskListResponse>>
    | PutEffect<UpdateTaskListAction | SetLoadingAction>;

function* getTaskListHandler({payload}: GetTaskListAction): IterableIterator<GetTaskListIteratorType> {
    const baseUrl: Nullable<string> = yield getBaseUrl();
    yield put(setLoading(true));
    const selectedJob: Nullable<JobEntity> = (yield select(
        (state: StoreState): Nullable<JobEntity> => state.job.selectedJob,
    )) as Nullable<JobEntity>;
    if (selectedJob) {
        const {projectId, jobId}: JobEntity = selectedJob;
        const axiosResponse: Nullable<AxiosResponse<GetTaskListResponse>> = yield axios.get(
            `${baseUrl}/project/${projectId}/job/${jobId}/task`,
            {
                params: {
                    ...payload?.cursor || null,
                    phaseName: payload?.phaseName,
                    phaseStatus: payload?.phaseStatus,
                    name: payload?.name,
                },
            },
        );

        if (axiosResponse) {
            const {data: {items, cursor}}: AxiosResponse<GetTaskListResponse> = axiosResponse;
            if (payload?.cursor) {
                if (items) {
                    const tasks: Nullable<Task[]> = yield select((state: StoreState): Task[] => state.task.list);
                    yield put(updateTaskList({
                        list: [...tasks!, ...items as Task[]],
                        cursor: cursor || payload?.cursor || null,
                    }));
                }
            } else {
                yield put(updateTaskList({
                    list: items as Task[] || [],
                    cursor: cursor || null,
                }));
            }
        }
    }
    yield put(setLoading(false));
}

export type GetTaskInfoIteratorType =
    SelectEffect
    | GetTokenGeneratorType
    | Promise<AxiosResponse<TaskInfo>>;

export function* getTaskInfoHandler({
    payload: {
        projectId, jobId, taskId, phase,
    },
}: GetTaskInfoAction): IterableIterator<GetTaskInfoIteratorType> {
    const annotationUrl: Nullable<string> = yield getAnnotationUrl();
    const taskToken: string | undefined = (yield getToken({
        projectId, jobId, taskId, phase,
    }));

    const axiosResponse: Nullable<AxiosResponse<TaskInfo>> = (yield axios.get(
        `${annotationUrl}/task/info?token=${taskToken || ""}`,
    )) as Nullable<AxiosResponse<TaskInfo>>;

    return axiosResponse?.data;
}

export function* taskSagaActionWatcher() {
    yield all([
        takeLatest(GET_TASK_LIST, safeSaga(getTaskListHandler)),
        takeLatest(GET_TASK_INFO, safeSaga(getTaskInfoHandler)),
    ]);
}
